import { Badge } from 'shadcn/components/ui/Badge';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { cn } from 'shadcn/lib/utils';

/**
 * Table displaying slices that can be applied to other configurations.
 * Allows selection/deselection of individual slices, including deleted ones.
 *
 * @param {Array} allSlices - All slices from form data (for index lookup)
 * @param {Array} applicableSlices - Array of slice objects that can be applied (incl. deleted)
 * @param {boolean} disabled - Whether selection is disabled
 * @param {() => void} onToggleAll - Callback to toggle all slices
 * @param {(sliceId: string) => void} onToggleSlice - Callback to toggle slice selection
 * @param {Set<string>} selectedSlices - Set of selected slice IDs
 * @returns {JSX.Element}
 */
function SlicesToApplyTable({ allSlices, applicableSlices, disabled, onToggleAll, onToggleSlice, selectedSlices }) {
    const allSelected = applicableSlices.length > 0 && applicableSlices.every((s) => selectedSlices.has(s.id));

    return (
        <div className="w-88 shrink-0 border rounded-lg overflow-hidden flex flex-col">
            <Table className="table-fixed [&_td]:py-2 [&_th]:py-2">
                <TableHeader className="bg-muted/50">
                    <TableRow>
                        <TableHead className="w-10">
                            <Checkbox
                                checked={allSelected}
                                onCheckedChange={onToggleAll}
                                disabled={disabled || applicableSlices.length === 0}
                            />
                        </TableHead>
                        <TableHead className="w-14 text-center">Pos</TableHead>
                        <TableHead>
                            <span className="flex items-center justify-between">
                                <span>Slice</span>
                                <span className="text-xs text-muted-foreground font-normal">
                                    {selectedSlices.size}/{applicableSlices.length}
                                </span>
                            </span>
                        </TableHead>
                    </TableRow>
                </TableHeader>
            </Table>
            <div className="flex-1 overflow-y-auto">
                <Table className="table-fixed [&_td]:py-2 [&_th]:py-2">
                    <TableBody>
                        {applicableSlices.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} className="text-center text-muted-foreground">
                                    No named slices to apply
                                </TableCell>
                            </TableRow>
                        ) : (
                            applicableSlices.map((slice) => {
                                const nameChanged = !slice.deleted && slice.name !== slice.originalName;
                                const sliceIndex = allSlices?.findIndex((s) => s.id === slice.id) ?? -1;

                                return (
                                    <TableRow
                                        key={slice.id}
                                        className="cursor-pointer"
                                        onClick={() => !disabled && onToggleSlice(slice.id)}
                                    >
                                        <TableCell className="w-10">
                                            <Checkbox
                                                checked={selectedSlices.has(slice.id)}
                                                onCheckedChange={() => onToggleSlice(slice.id)}
                                                disabled={disabled}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </TableCell>
                                        <TableCell className="w-14 text-center text-muted-foreground">
                                            {slice.deleted ? '—' : sliceIndex + 1}
                                        </TableCell>
                                        <TableCell className="max-w-0">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <div
                                                    className={cn(
                                                        'text-sm truncate',
                                                        slice.deleted && 'line-through text-muted-foreground',
                                                    )}
                                                    title={slice.name}
                                                >
                                                    {slice.name}
                                                </div>
                                                {slice.deleted && (
                                                    <Badge variant="outline" className="shrink-0">
                                                        Delete
                                                    </Badge>
                                                )}
                                            </div>
                                            {nameChanged && (
                                                <div
                                                    className="text-xs text-muted-foreground truncate"
                                                    title={`Was: ${slice.originalName}`}
                                                >
                                                    Was: {slice.originalName}
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

export default SlicesToApplyTable;
